import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { map, catchError, shareReplay, finalize } from 'rxjs/operators';

export interface InvoiceItem {
  id: number;
  description: string;
  value: number;
}

export interface Invoice {
  id: string;
  sellerGroupId: string;
  invoiceNumber: number;
  status: number;
  isOverdue: boolean;
  createdDate: string;
  updatedDate: string;
  dueDate: string;
  totalAmount: number;
  items: InvoiceItem[];
}

export interface InvoiceResponse {
  items: Invoice[];
  total: number;
}

export interface PixResponse {
  qrCodeBase64: string;
  ticketUrl: string;
}

export interface PaymentStatusResponse {
  isPaid: boolean;
  paymentStatus: string;
  invoiceStatus: string;
}

export interface OverdueInvoicesResponse {
  hasOverdue: boolean;
  total: number;
  upcomingDueCount: number;
  dueTodayCount: number;
  blockAccess?: boolean;
}

export interface PendingPaymentResponse {
  hasPendingPayment: boolean;
  pixResponse?: PixResponse;
}

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private apiUrl = `${environment.apiUrl}/invoice`;

  // Cache do status de faturas vencidas. Evita que cada navegação (guards)
  // dispare uma chamada ao /overdue. O polling do app.component atualiza esse
  // cache a cada 10 min via forceRefresh; entre os polls os guards reaproveitam
  // o resultado em memória. TTL maior que o intervalo de polling garante que
  // nenhuma navegação precise ir à rede por conta própria.
  private overdueRequest$: Observable<OverdueInvoicesResponse> | null = null;
  private overdueRequestTime = 0;
  // true enquanto há uma requisição ao /overdue ainda em andamento. Garante que
  // chamadas concorrentes (vários pollers/guards no mesmo tick) reaproveitem a
  // mesma requisição em vez de cada uma abrir uma nova — só 1 chamada sai à rede.
  private overdueInFlight = false;
  private readonly OVERDUE_CACHE_TTL = 12 * 60 * 1000; // 12 minutos
  // Liga/desliga toda a busca de faturas vencidas (/overdue). Quando false,
  // getOverdueInvoices retorna "sem vencidas" sem chamar a rede.
  private readonly OVERDUE_SEARCH_ENABLED = true;

  constructor(private http: HttpClient) {}

  getInvoices(skip: number, limit: number): Observable<InvoiceResponse> {
    const url = `${this.apiUrl}?skip=${skip}&limit=${limit}`;

    return this.http.get<InvoiceResponse>(url, { observe: 'response' }).pipe(
      map(response => {
        if (response.status !== 200) {
          throw new Error('Erro ao buscar faturas.');
        }
        return response.body || { items: [], total: 0 };
      })
    );
  }

  generatePix(invoiceId: string, email: string, firstName: string, lastName: string, idempotencyKey: string): Observable<PixResponse> {
    const url = `${this.apiUrl}/${invoiceId}/generate-pix`;
    const body = {
      email,
      firstName,
      lastName,
      idempotencyKey
    };

    return this.http.post<PixResponse>(url, body, { observe: 'response' }).pipe(
      map(response => {
        if (response.status !== 200 && response.status !== 201) {
          throw new Error('Erro ao gerar PIX.');
        }
        return response.body || { qrCodeBase64: '', ticketUrl: '' };
      })
    );
  }

  checkPaymentStatus(invoiceId: string): Observable<PaymentStatusResponse> {
    const url = `${this.apiUrl}/${invoiceId}/check-payment-status`;
    
    return this.http.post<PaymentStatusResponse>(url, {}, { observe: 'response' }).pipe(
      map(response => {
        if (response.status === 200 && response.body) {
          return response.body;
        }
        return { isPaid: false, paymentStatus: '', invoiceStatus: '' };
      })
    );
  }

  /**
   * Retorna o status de faturas vencidas.
   * Por padrão usa um cache em memória (TTL) e deduplica requisições em voo,
   * para que navegações entre páginas não gerem novas chamadas ao /overdue.
   * @param forceRefresh quando true, ignora o cache e busca da rede (usado
   *   apenas pelo polling periódico e pela verificação inicial).
   */
  getOverdueInvoices(forceRefresh = false): Observable<OverdueInvoicesResponse> {
    // Com a busca desativada (OVERDUE_SEARCH_ENABLED = false), retorna sempre
    // "sem vencidas" sem tocar a rede, o que também impede o polling do
    // app.component e o overdueBlockGuard de chamarem o endpoint.
    if (!this.OVERDUE_SEARCH_ENABLED) {
      return of({ hasOverdue: false, total: 0, upcomingDueCount: 0, dueTodayCount: 0, blockAccess: false });
    }

    const now = Date.now();
    const isExpired = (now - this.overdueRequestTime) >= this.OVERDUE_CACHE_TTL;

    // Se já há uma requisição em andamento, reaproveita-a SEMPRE (inclusive com
    // forceRefresh). Assim, se vários pollers/guards dispararem no mesmo instante,
    // apenas UMA chamada ao /overdue sai para a rede.
    if (this.overdueInFlight && this.overdueRequest$) {
      return this.overdueRequest$;
    }

    if (forceRefresh || !this.overdueRequest$ || isExpired) {
      const url = `${this.apiUrl}/overdue`;
      this.overdueRequestTime = now;
      this.overdueInFlight = true;
      this.overdueRequest$ = this.http.get<OverdueInvoicesResponse>(url, { observe: 'response' }).pipe(
        map(response => {
          if (response.status === 200 && response.body) {
            return response.body;
          }
          return { hasOverdue: false, total: 0, upcomingDueCount: 0, dueTodayCount: 0, blockAccess: false };
        }),
        catchError(err => {
          // Não cacheia erro: invalida para permitir nova tentativa na próxima chamada.
          this.overdueRequest$ = null;
          this.overdueRequestTime = 0;
          return throwError(() => err);
        }),
        finalize(() => {
          // Requisição concluída (sucesso/erro): libera para futuros refreshes.
          this.overdueInFlight = false;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.overdueRequest$;
  }

  isAccessBlockedByOverdue(): Observable<boolean> {
    return this.getOverdueInvoices().pipe(
      map(r => r.blockAccess === true)
    );
  }

  getPendingPayment(invoiceId: string): Observable<PendingPaymentResponse> {
    const url = `${this.apiUrl}/${invoiceId}/pending-payment`;
    
    return this.http.get<PendingPaymentResponse>(url, { observe: 'response' }).pipe(
      map(response => {
        if (response.status === 200 && response.body) {
          return response.body;
        }
        return { hasPendingPayment: false };
      })
    );
  }
}

